<script>
  // d3 stuff 
  import { area, stack } from 'd3-shape';
  import { extent } from 'd3-array';
  import { axisBottom, axisLeft } from 'd3-axis';
  import { scaleLinear, scaleUtc } from 'd3-scale';
  import { monthTickValues, monthFormat } from '../utils/d3-utils';

  // Components
  import Axis from './Axis.svelte';
  import Annotation from './Annotation.svelte';
  import Sourceline from './Sourceline.svelte';
  import ChartToggle from './ChartToggle.svelte';
  import ChartHeadline from './ChartHeadline.svelte';

  // Other imports
  import { last } from '../utils/last';
  import { stripHtml } from '../utils/strip';
  import { slugify } from '../utils/slugify';
  import { randomString } from '../utils/random';
  import { intcomma } from 'journalize';

  // Data configuration
  export let title;
  export let data;
  export let attrs;
  export let sourceline;
  export let selectedAttrIndex = 0;
  $: selectedAttr = attrs[selectedAttrIndex];

  // Dimensions
  export let width;
  export let height = 300;
  export let margin = { top: 20, right: 10, bottom: 35, left: 65 };

  // Reactive data
  $: allKeys = attrs.map(d => d.layers.map(e => e.value));
  $: selectedKeys = selectedAttr.layers.map(a => a.value);
  $: selectedFillColors = selectedAttr.layers.reduce((result, obj) => {
        result[obj.value] = obj.color;
        return result;
     }, {});
  $: selectedData = data.map((a) => {
    let b = {date: a.date};
    selectedAttr.layers.forEach(c => {
        b[c.value] = a[c.value];
    });
    return b;
  });
  $: maxValue = selectedAttr.maxValue;
  $: stackedData = stack().keys(selectedKeys)(selectedData);
  $: annotations = selectedAttr.annotations || [];

  // Title
  const titleId = `${randomString()}`;
  $: title = stripHtml(selectedAttr.headline);

  // Scales
  $: xScale = scaleUtc()
    .domain(extent(selectedData, (d) => d.date))
    .range([0, width - margin.left - margin.right]);

  $: yScaleType = selectedAttr.yScaleType || scaleLinear;
  $: yMin = selectedAttr.yMin || 0
  $: yScale = yScaleType()
      .domain([yMin, maxValue])
      .nice()
      .range([height - margin.top - margin.bottom, 0]);

  // Shapes
  $: drawArea = area()
    .x((d) => xScale(d.data.date))
    .y0((d) => yScale(d[0]))
    .y1((d) => yScale(d[1]));

  // Axes
  export let getXTicks = (a) => {
    const xExtent = extent(a.map((d) => d.date));
    return monthTickValues(...xExtent);
  };
  export let formatYTicks = intcomma;
  export let formatXTicks = monthFormat;

  // Events
  const handleToggle = (e) => {
    selectedAttrIndex = attrs.findIndex((d, i) => i == e.target.getAttribute('data-index'));
  }
</script>

{#if attrs.length > 1}
<div class="button-grid button-group">
  <div class="button-grid-group">
    <ChartToggle
      slug={`${slugify(title)}-toggle`}
      buttonList={attrs}
      buttonGroupClass='button-group'
      bind:selectedIndex={selectedAttrIndex}
      on:click={handleToggle}
    />
  </div>
</div>
{/if}

<ChartHeadline text={selectedAttr.headline} />

<div class="inner-container" bind:clientWidth={width}>
  <svg {width} {height} role="graphics-document" aria-labelledby="{titleId}" class="area-timeseries">
    <title id="{titleId}">{title}</title>
    <g transform="translate({margin.left},{margin.top})">
      <g class="axes">
        <Axis
          axis={axisLeft}
          scale={yScale}
          className="axis--y"
          tickSize={-(width - margin.left - margin.right)}
          tickFormat={formatYTicks}
          ticks="5"
        />
        <Axis
          axis={axisBottom}
          scale={xScale}
          className="axis--x"
          yTranslate={height - margin.top - margin.bottom}
          tickFormat={formatXTicks}
          tickValues={getXTicks(selectedData)}
        />
      </g>
      <g class="area" data-data="{stackedData}" >
        {#each stackedData as layer, idx}
            <path data-idx={idx} role="graphics-symbol" d="{drawArea(layer)}" fill={selectedFillColors[layer.key]} opacity="0.85" />
        {/each}
      </g>
      {#if annotations}
      <g class="annotations">
      {#each annotations as anno}
         <Annotation x={xScale(anno.x)} y={yScale(anno.y)} text={anno.text} anchor={anno.anchor} />
      {/each}
      </g>
      {/if}
    </g>
  </svg>
</div>

<Sourceline text={sourceline} />
